// Shared aggregation logic for the append-only feedback_reaction event
// log. Used by both the message-inbox (per InboxGroup) and the form-
// submission data layer (per submission's sibling Pool row). Kept in
// its own module so the data layer can call it without dragging in the
// useFeedbackInbox hook's React surface.

import { z } from 'zod';

export const ReactionEventSchema = z.object({
  emoji: z.object({
    id: z.string().nullable(),
    name: z.string().nullable(),
    animated: z.boolean().nullable(),
    identifier: z.string(),
  }),
  action: z.enum(['add', 'remove']),
  observed_at: z.string(),
  reactor_external_id: z.string(),
});

export type ReactionEvent = z.infer<typeof ReactionEventSchema>;

export interface ReactionTotal {
  identifier: string;
  emoji: {
    id: string | null;
    name: string | null;
    animated: boolean | null;
  };
  count: number;
  // True iff this is the most-recently-added reaction in the input set
  // AND its observed_at equals the latest 'add'. Drives the tonal
  // border on the reaction pill in the row component.
  isLatest: boolean;
}

export interface ReactionAggregate {
  totals: ReactionTotal[];
  latestAddObservedAt: string | null;
  latestAddIdentifier: string | null;
}

// Sort events ascending so the latest action per (reactor, emoji) wins
// via Map overwrite, then count adds. Track the latest 'add' globally
// so the caller can flag which emoji "made" the latest activity bump.
export function aggregateReactions(events: ReactionEvent[]): ReactionAggregate {
  const reactorByEmoji = new Map<string, Map<string, 'add' | 'remove'>>();
  const emojiByIdentifier = new Map<string, ReactionTotal['emoji']>();
  let latestAddObservedAt: string | null = null;
  let latestAddIdentifier: string | null = null;

  const sorted = [...events].sort((a, b) => a.observed_at.localeCompare(b.observed_at));
  for (const e of sorted) {
    const id = e.emoji.identifier;
    emojiByIdentifier.set(id, {
      id: e.emoji.id,
      name: e.emoji.name,
      animated: e.emoji.animated,
    });
    let reactors = reactorByEmoji.get(id);
    if (reactors === undefined) {
      reactors = new Map();
      reactorByEmoji.set(id, reactors);
    }
    reactors.set(e.reactor_external_id, e.action);
    if (e.action === 'add') {
      if (latestAddObservedAt === null || e.observed_at > latestAddObservedAt) {
        latestAddObservedAt = e.observed_at;
        latestAddIdentifier = id;
      }
    }
  }

  const totals: ReactionTotal[] = [];
  for (const [identifier, reactors] of reactorByEmoji) {
    let count = 0;
    for (const action of reactors.values()) if (action === 'add') count += 1;
    if (count > 0) {
      totals.push({
        identifier,
        emoji: emojiByIdentifier.get(identifier) ?? { id: null, name: null, animated: null },
        count,
        isLatest: false,
      });
    }
  }
  totals.sort((a, b) => b.count - a.count);

  if (latestAddIdentifier !== null) {
    const target = totals.find((t) => t.identifier === latestAddIdentifier);
    if (target !== undefined) target.isLatest = true;
  }

  return { totals, latestAddObservedAt, latestAddIdentifier };
}
